import { LocalDate } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dynVariadic } from "trader.ts/ui";
import { Field, Form, constantField, selectBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollmentid,
  expectedpaymentid,
  internalteacherid,
  periodid,
  studentid,
  thirdpartysellerid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import { renderCancelButton, renderSaveButton } from "../../client/savebutton";
import { invoiceSubjectToEnrollmentId } from "../../domain/finance/types";
import { period } from "../../domain/periods";
import { isEqual } from "../../lib/isequal";
import {
  ddMMyyyy,
  renderCurrency,
  renderedInvoiceSubjectToHtml,
  renderFirstNameLastName,
  renderInvoiceSubjectForOverviews,
} from "../../render";
import * as routes from "../../routes";
import { renderPeriod } from "../../translations_specific";
import {
  invoiceSubjectFilterField,
  renderReportResult,
  runQueryOnFormChange,
} from "../finance_invoices/client";

const green = "#b9f5a7";
const red = "#ffb48e";

type row = GetReturnTypeFromApiSpec<
  typeof routes.queryNonPeriodicPaymentsStatisticSpec
>[number];

type possibleSorting =
  | "student"
  | "enrollment"
  | "date"
  | "last_reminder"
  | "amount_total"
  | "amount_left";

export function render(opts: {
  periods: period[];
  internalTeachers: {
    firstname: string;
    lastname: string;
    internalteacherid: internalteacherid;
  }[];
  hasPeriodFees: boolean;
  thirdPartySellers: { id: thirdpartysellerid; name: string }[];
}): HTMLElement | HTMLElement[] {
  const today = LocalDate.now();

  const form = new Form({})
    .addField("period", [], async () => {
      const values: (
        | { tag: "no filter" }
        | {
            tag: "period";
            id: periodid;
            firstday: LocalDate;
            lastday: LocalDate;
          }
      )[] = [
        { tag: "no filter" as const },
        ...opts.periods.map((p) => ({ tag: "period" as const, ...p })),
      ];
      return selectBox({
        initial: values[0],
        values: values,
        show: (p) =>
          p.tag === "no filter"
            ? "Geen filter"
            : renderPeriod(p.firstday, p.lastday),
        label: "Schooljaar",
        // render: radioFieldCheckboxRender,
      });
    })
    .addField("paid", [], async () =>
      selectBox({
        values: ["OnlyUnpaid" as const, "All" as const],
        initial: "OnlyUnpaid" as const,
        show: (s) =>
          s === "All"
            ? "Allemaal"
            : s === "OnlyUnpaid"
            ? "Alleen onbetaalde"
            : checkAllCasesHandled(s),
        // render: radioFieldCheckboxRender,
        label: "Betaalstatus",
      }),
    )
    .addField("overdue", ["paid"], async ({ paid }) =>
      paid === "OnlyUnpaid"
        ? selectBox({
            values: ["OnlyOverdue" as const, "All" as const],
            initial: "All" as const,
            show: (s) =>
              s === "All"
                ? "Allemaal"
                : s === "OnlyOverdue"
                ? "Termijn overschreden"
                : checkAllCasesHandled(s),
            // render: radioFieldCheckboxRender,
            label: "Termijn",
          })
        : constantField("All" as const),
    )
    .addField("thirdpartyseller", [], async () => {
      return thirdPartySellerField(opts);
    })
    .addField("subject", [], async () => invoiceSubjectFilterField(opts))
    .build();

  const res = runQueryOnFormChange(
    form.s,
    routes.queryNonPeriodicPaymentsStatisticSpec,
    (t) => t,
  );

  const id = "classy-table-id";

  const sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }> = new Source({ sorting: "student", direction: "asc" });

  return (
    <main style="max-width: unset;">
      <section className={style.equal_columns + style.cancel_main_margins}>
        {form.render()}
      </section>
      {renderReportResult(res, (rows): HTMLElement[] => {
        const selectingS = new Source(false);
        return dynVariadic([selectingS, sortingS], ([selecting, sorting]) => {
          const allSelectable = rows.map((r) => ({
            enrollmentid: invoiceSubjectToEnrollmentId(r.subject),
            studentid: r.subject.student.id,
            expectedpaymentid: r.id,
          }));
          const selectedS = new Source(selecting ? allSelectable : []);
          const downloadLinks = renderTableDownloadLinks({
            tableid: id,
            filename: "OverzichtBetalingen",
          });
          const actions = renderSelectionButtons(
            selecting,
            selectingS,
            allSelectable,
            selectedS,
          );

          const headerRow = renderHeaderRow(
            selecting,
            selectedS,
            rows,
            allSelectable,
            sorting,
            sortingS,
          );

          const renderedRows =
            rows === null
              ? [<tr></tr>]
              : rows
                  .orderBy(doSorting(sorting.sorting), sorting.direction)
                  .map((r) => renderRow(today, selecting, selectedS, r));
          const renderedTotal =
            rows === null ? (
              <span></span>
            ) : (
              <tr style="font-weight: bold;background-color: var(--grey-100);">
                <td>{"Totaal" + ": " + rows.length}</td>
                <td style="display: none"></td>
                <td style="display: none"></td>
                <td style="display: none"></td>
                <td style="display: none"></td>
                <td></td>
                <td></td>
                <td></td>
                <td style="width: 100px;">
                  {renderCurrency(
                    rows.reduce((acc, row) => acc.add(row.amount), currency(0)),
                  )}
                </td>
                <td style="width: 100px;">
                  {renderCurrency(
                    rows.reduce(
                      (acc, row) => acc.add(row.amount_left),
                      currency(0),
                    ),
                  )}
                </td>
                {selecting ? <td></td> : []}
              </tr>
            );
          return (
            <div>
              {actions}
              {downloadLinks}
              <table id={id}>
                {headerRow}
                {renderedRows}
                {renderedTotal}
              </table>
            </div>
          );
        });
      })}
    </main>
  );
}

export function thirdPartySellerField(opts: {
  thirdPartySellers: { id: thirdpartysellerid; name: string }[];
  initialValue?:
    | {
        tag: "thirdpartyseller";
        item: {
          id: thirdpartysellerid;
          name: string;
        };
      }
    | { tag: "no_filter" }
    | { tag: "none" }
    | null;
}): Promise<
  Field<
    | {
        tag: "thirdpartyseller";
        item: {
          id: thirdpartysellerid;
          name: string;
        };
      }
    | { tag: "no_filter" }
    | { tag: "none" }
  >
> {
  const values = [
    { tag: "no_filter" as const },
    { tag: "none" as const },
    ...opts.thirdPartySellers.map((s) => ({
      tag: "thirdpartyseller" as const,
      item: s,
    })),
  ];
  return opts.thirdPartySellers.length === 0
    ? constantField({ tag: "no_filter" as const })
    : selectBox({
        initial: opts.initialValue || values[0],
        values: values,
        show: (n) =>
          n.tag === "no_filter"
            ? "Allemaal"
            : n.tag === "none"
            ? "Geen vereniging"
            : n.item.name,
        // render: radioFieldCheckboxRender,
        label: "Vereniging",
      });
}

function doSorting(s: possibleSorting): (r: row) => number | string {
  return (r: row) => {
    if (s === "student") {
      return renderFirstNameLastName(r.subject.student);
    } else if (s === "enrollment") {
      return r.subject.tag === "enrollment"
        ? renderFirstNameLastName(r.subject.teacher)
        : "aaa";
    } else if (s === "date") {
      return r.date.toEpochDay();
    } else if (s === "last_reminder") {
      return r.last_reminder_sent === null
        ? 0
        : r.last_reminder_sent.toEpochDay();
    } else if (s === "amount_total") {
      return r.amount.value;
    } else if (s === "amount_left") {
      return r.amount_left.value;
    } else {
      return checkAllCasesHandled(s);
    }
  };
}

function renderRow(
  today: LocalDate,
  selecting: boolean,
  selectedS: Source<
    {
      enrollmentid: enrollmentid | null;
      studentid: studentid;
      expectedpaymentid: expectedpaymentid;
    }[]
  >,
  row: row,
) {
  return (
    <tr>
      <td>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: row.subject.student.id,
          })}
        >
          {renderFirstNameLastName(row.subject.student)}
        </a>
      </td>
      <td style="display: none">{showEmail(row.student_emails, 0)}</td>
      <td style="display: none">{showEmail(row.student_emails, 1)}</td>
      <td style="display: none">{showEmail(row.student_emails, 2)}</td>
      <td style="display: none">{showEmail(row.student_emails, 3)}</td>
      <td>
        {renderedInvoiceSubjectToHtml(
          renderInvoiceSubjectForOverviews(row.subject),
        )}
      </td>
      <td style={row.date.isAfter(today) ? "" : "color: red;"}>
        {row.date.format(ddMMyyyy)}
      </td>
      <td>
        {row.last_reminder_sent ? row.last_reminder_sent.format(ddMMyyyy) : ""}
      </td>
      <td>
        <a
          href={routes.invoicedetailsRoute.link({
            invoiceid: row.invoiceid,
            edit: false,
          })}
        >
          {renderCurrency(row.amount)}
        </a>
      </td>
      <td
        style={`background-color: ${
          row.amount_left.value === 0 ? green : red
        };`}
      >
        {renderCurrency(row.amount_left)}
      </td>
      {selecting
        ? dynVariadic([selectedS], ([selected]) => {
            const checked = selected.some(
              (sel) => sel.expectedpaymentid === row.id,
            );
            const onChange = () =>
              selectedS.set(
                selected.some((sel) => isEqual(sel.expectedpaymentid, row.id))
                  ? selected.filter(
                      (i) => !isEqual(i.expectedpaymentid, row.id),
                    )
                  : selected.concat({
                      enrollmentid: invoiceSubjectToEnrollmentId(row.subject),
                      studentid: row.subject.student.id,
                      expectedpaymentid: row.id,
                    }),
              );
            return (
              <td>
                <label
                  className={style.input_like + style.checkbox}
                  style="margin-bottom: 0px;"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onchange={onChange}
                  />
                  <span>{"Selecteer"}</span>
                </label>
              </td>
            );
          })
        : []}
    </tr>
  );
}

function renderHeaderRow(
  selecting: boolean,
  selectedS: Source<
    {
      enrollmentid: enrollmentid | null;
      studentid: studentid;
      expectedpaymentid: expectedpaymentid;
    }[]
  >,
  rows: unknown[],
  allSelectables: {
    enrollmentid: enrollmentid | null;
    studentid: studentid;
    expectedpaymentid: expectedpaymentid;
  }[],
  currentSorting: {
    sorting: possibleSorting;
    direction: "asc" | "desc";
  },
  sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }>,
) {
  return (
    <tr style="position: sticky; top: 0px; background-color: white;">
      {renderTableHeaderCellWithSorting(
        "Student",
        "student",
        currentSorting,
        sortingS,
      )}
      {renderTableHeaderCellWithSorting(
        "Emailadres" + " 1",
        null,
        currentSorting,
        sortingS,
        "display: none;",
      )}
      {renderTableHeaderCellWithSorting(
        "Emailadres" + " 2",
        null,
        currentSorting,
        sortingS,
        "display: none;",
      )}
      {renderTableHeaderCellWithSorting(
        "Emailadres" + " 3",
        null,
        currentSorting,
        sortingS,
        "display: none;",
      )}
      {renderTableHeaderCellWithSorting(
        "Emailadres" + " 4",
        null,
        currentSorting,
        sortingS,
        "display: none;",
      )}
      {renderTableHeaderCellWithSorting(
        "Les",
        "enrollment",
        currentSorting,
        sortingS,
      )}
      {renderTableHeaderCellWithSorting(
        "Betalingsdatum",
        "date",
        currentSorting,
        sortingS,
      )}
      {renderTableHeaderCellWithSorting(
        "Laatste %newline herinnering".replace("%newline", "\n"),
        "last_reminder",
        currentSorting,
        sortingS,
        "white-space: pre-line;",
      )}
      {renderTableHeaderCellWithSorting(
        "Bedrag" + " €",
        "amount_total",
        currentSorting,
        sortingS,
      )}
      {renderTableHeaderCellWithSorting(
        "Nog te betalen" + " €",
        "amount_left",
        currentSorting,
        sortingS,
      )}
      {selecting ? (
        <th>
          <a
            onclick={() => {
              const currL = selectedS.get().length;
              selectedS.set(currL === rows.length ? [] : allSelectables);
            }}
          >
            {"Alles selecteren"}
          </a>
        </th>
      ) : (
        []
      )}
    </tr>
  );
}

function renderSelectionButtons(
  selecting: boolean,
  selectingS: Source<boolean>,
  allSelectable: {
    enrollmentid: enrollmentid | null;
    studentid: studentid;
    expectedpaymentid: expectedpaymentid;
  }[],
  selectedS: Source<
    {
      enrollmentid: enrollmentid | null;
      studentid: studentid;
      expectedpaymentid: expectedpaymentid;
    }[]
  >,
) {
  return (
    <div style="text-align: right">
      {selecting ? (
        [
          renderCancelButton({
            onclick: () => selectingS.set(false),
          }),
          renderSaveButton({
            save: () => {
              return rpc(
                routes.sendPaymentRemindersNonPeriodicSpec,
                undefined,
                { ids: selectedS.get() },
              );
            },
            text: {
              normal: "Zet emails in wachtrij",
              success: "Gelukt!",
            },
            timeout: 3000,
            reload: () => selectingS.set(false),
          }),
        ]
      ) : (
        <button
          className={style.button_accent}
          onclick={() => {
            selectingS.set(true);
            selectedS.set(allSelectable);
          }}
        >
          {"Stuur herinneringsmails"}
        </button>
      )}
    </div>
  );
}

function showEmail(emails: { email: string }[], index: number) {
  if (emails && emails[index]) {
    return emails[index].email;
  }
  return "";
}

export function renderTableHeaderCellWithSorting<SORTING>(
  label: string,
  sorting: SORTING | null,
  currentSorting: {
    sorting: SORTING;
    direction: "asc" | "desc";
  },
  sortingS: Source<{
    sorting: SORTING;
    direction: "asc" | "desc";
  }>,
  extraStyle?: string,
) {
  const onclick =
    sorting === null
      ? null
      : () => {
          // eslint-disable-next-line
          if (currentSorting.sorting === sorting) {
            sortingS.set({
              sorting: sorting,
              direction: reverseSortingDirection(currentSorting.direction),
            });
          } else {
            sortingS.set({ sorting: sorting, direction: "asc" });
          }
        };
  const i =
    // eslint-disable-next-line
    sorting !== null && currentSorting.sorting === sorting ? (
      <i
        className={FA.icon_chevron_circle_down_solid}
        style={
          "margin-left: 8px;" +
          (currentSorting.direction === "asc"
            ? ""
            : "transform: rotate(180deg); position: relative; top: -1px;")
        }
      ></i>
    ) : (
      <span></span>
    );
  return (
    <th
      style={
        "vertical-align: bottom; " +
        (onclick ? "cursor: pointer; " : "") +
        (extraStyle || "")
      }
    >
      {onclick ? <a onclick={onclick}>{label}</a> : label}
      {i}
    </th>
  );

  function reverseSortingDirection(a: "asc" | "desc"): "asc" | "desc" {
    return a === "asc" ? "desc" : "asc";
  }
}
